import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../services/supabase';
import { useLovense } from '../../hooks/useLovense';
import { SafeWordButton } from '../SafeWordButton';
import toast from 'react-hot-toast';

const TRUTHS = [
  "What's your biggest turn-on?",
  "Describe your favorite intimate memory with your partner",
  "What's something you've always wanted to try?",
  "What's your partner's best physical feature?",
  "What's the most adventurous place you've done it?",
  "What's your favorite thing your partner does?",
  "What's your secret fantasy?",
  "When did you know you wanted to be intimate with your partner?",
  "What's your favorite time of day for intimacy?",
  "What's something that always puts you in the mood?"
];

const DARES = [
  "Kiss your partner's neck for 30 seconds",
  "Whisper something dirty in your partner's ear",
  "Give your partner a sensual massage for 1 minute",
  "Blindfold your partner and tease them",
  "Do a striptease to your partner",
  "Kiss your partner somewhere unexpected",
  "Feed your partner something sensually",
  "Give your partner goosebumps without touching their genitals",
  "Describe in detail what you want to do to your partner later",
  "Hold eye contact with your partner for 60 seconds while touching yourself"
];

export const TruthDare = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const role = (searchParams.get('role') || 'A') as 'A' | 'B';
  
  const { vibrate, setPattern } = useLovense();
  
  const [currentPrompt, setCurrentPrompt] = useState<string | null>(null);
  const [promptType, setPromptType] = useState<'truth' | 'dare' | null>(null);
  const [isMyTurn, setIsMyTurn] = useState(role === 'A');
  const [waitingForPartner, setWaitingForPartner] = useState(false);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    const channel = supabase.channel(`game-${sessionId}`);
    channelRef.current = channel;
    
    channel
      .on('broadcast', { event: 'game_action' }, ({ payload }: any) => {
        const { action } = payload;
        if (action.playerId !== role) {
          if (action.action === 'question_answered') {
            toast.success('Partner answered!');
            vibrate(8);
            setIsMyTurn(true);
            setWaitingForPartner(false);
          }
          if (action.action === 'dare_completed') {
            toast.success('Partner completed their dare! 🎉');
            setPattern('Fireworks');
            setIsMyTurn(true);
            setWaitingForPartner(false);
          }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, role, vibrate, setPattern]);

  const pickRandom = (array: string[]) => array[Math.floor(Math.random() * array.length)];

  const selectTruth = useCallback(() => {
    setPromptType('truth');
    setCurrentPrompt(pickRandom(TRUTHS));
    setIsMyTurn(false);
  }, []);

  const selectDare = useCallback(() => {
    setPromptType('dare');
    setCurrentPrompt(pickRandom(DARES));
    setIsMyTurn(false);
  }, []);

  const completePrompt = useCallback(() => {
    if (promptType === 'truth') {
      channelRef.current?.send({
        type: 'broadcast',
        event: 'game_action',
        payload: { action: { playerId: role, action: 'question_answered' }, response: { truthful: true } }
      });
    } else {
      channelRef.current?.send({
        type: 'broadcast',
        event: 'game_action',
        payload: { action: { playerId: role, action: 'dare_completed' }, response: {} }
      });
    }
    setCurrentPrompt(null);
    setPromptType(null);
    setWaitingForPartner(true);
  }, [promptType, role]);

  const skipPrompt = useCallback(() => {
    setCurrentPrompt(null);
    setPromptType(null);
    setIsMyTurn(true);
  }, []);

  return (
    <div className="game-container">
      <div style={{ textAlign: 'center' }}>
        <h1>Truth or Dare</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Take turns choosing truths or dares
        </p>
      </div>

      <AnimatePresence mode="wait">
        {currentPrompt ? (
          <motion.div
            key="prompt"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="card"
            style={{ maxWidth: '500px', textAlign: 'center' }}
          >
            <div style={{ 
              display: 'inline-block',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              background: promptType === 'truth' ? 'var(--primary)' : 'var(--secondary)',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              textTransform: 'uppercase'
            }}>
              {promptType}
            </div>
            
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', lineHeight: 1.6 }}>
              {currentPrompt}
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={skipPrompt}>
                Skip
              </button>
              <button className="btn btn-primary btn-lg" onClick={completePrompt}>
                {promptType === 'truth' ? 'Answered' : 'Completed'} ✓
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="selection"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', justifyContent: 'center' }}
          >
            {isMyTurn ? (
              <>
                <motion.button
                  className="btn btn-primary btn-lg"
                  onClick={selectTruth}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: 'var(--border-radius)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1rem'
                  }}
                >
                  <span style={{ fontSize: '3rem' }}>🤔</span>
                  <span>Truth</span>
                </motion.button>

                <motion.button
                  className="btn btn-secondary btn-lg"
                  onClick={selectDare}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: 'var(--border-radius)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1rem',
                    background: 'var(--secondary)'
                  }}
                >
                  <span style={{ fontSize: '3rem' }}>😈</span>
                  <span>Dare</span>
                </motion.button>
              </>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="pulse" style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                  ⏳
                </div>
                <h3>Waiting for partner...</h3>
                <p style={{ color: 'var(--text-muted)' }}>
                  Your partner is completing their turn
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <SafeWordButton sessionId={sessionId!} />
    </div>
  );
};